import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Peer from 'simple-peer';
import api from '../services/api';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';

// Sur Cordova/Android, les assets sont dans file:///android_asset/www/
// Sur navigateur, les chemins sont relatifs à la racine
const BASE = window.cordova
  ? '' // chemin relatif — Cordova résout depuis www/
  : '';




const VideoCall = ({ socket, setCallInProgress, prompt }) => {

  const [isMobile]        = useState(window.innerWidth < 768);
  const location          = useLocation();
  const navigate          = useNavigate();
  const { t }             = useTranslation();
const BACKGROUNDS = [
  { id: 'none',   label: t('discussion.aucun'),   src: null },
  { id: 'blur',   label: t('discussion.flou'),    src: 'blur' },
  { id: 'office', label: t('discussion.espace'),  src: `${BASE}backgrounds/espace.jpg` },
  { id: 'nature', label: t('discussion.nature'),  src: `${BASE}backgrounds/nature.jpg` },
  { id: 'ocean', label: t('discussion.ocean'),  src: `${BASE}backgrounds/ocean.jpg` },
];


  const { discussionId, participants: initialParticipants, isCaller } = location.state || {};
  const user     = JSON.parse(localStorage.getItem('user'));
  const myUserId = String(user.id);

  const [participants,  setParticipants]  = useState(initialParticipants || []);
  const [peers,         setPeers]         = useState({});
  const [videoEnabled,  setVideoEnabled]  = useState(true);
  const [audioEnabled,  setAudioEnabled]  = useState(true);
  const [callDuration,  setCallDuration]  = useState(0);
  const [bgId,          setBgId]          = useState('none');
  const [bgPanelOpen,   setBgPanelOpen]   = useState(false);
  const [bgReady,       setBgReady]       = useState(false);

  const timerRef            = useRef(null);
  const localVideoRef       = useRef();
  const rawVideoRef         = useRef();
  const canvasRef           = useRef();
  const remoteVideoRefs     = useRef({});
  const peersRef            = useRef({});
  const segmentationRef     = useRef(null);
  const bgImageRef          = useRef(null);
  const animFrameRef        = useRef(null);
  const bgIdRef             = useRef('none');

  // Streams séparés — clé de la solution audio
  const videoStreamRef      = useRef(null); // canvas video uniquement
  const audioStreamRef      = useRef(null); // micro uniquement
  const rawVideoStreamRef   = useRef(null); // caméra brute pour MediaPipe
  const outgoingStreamRef = useRef(null); // stream combiné vidéo+audio envoyé aux peers
  const audioCtxRef       = useRef(null);
  const bassFilterRef     = useRef(null);
  const trebleFilterRef   = useRef(null);
  const rawAudioTrackRef  = useRef(null); // track brute, utilisée pour toggleAudio

  const [customBgUrl, setCustomBgUrl] = useState(null);
  const fileInputRef = useRef(null);

  const [speakingMap, setSpeakingMap] = useState({}); // { userId: boolean }
const analysersRef = useRef({}); // { userId: AnalyserNode }
const speakingRafRef = useRef(null);

//const [subtitlesEnabled, setSubtitlesEnabled] = useState(false);
//const [subtitles, setSubtitles] = useState({}); // { userId: { text, timestamp } }
//const recognitionRef = useRef(null);
//const subtitleTimeoutRef = useRef({});

const [isRecording, setIsRecording] = useState(false);
const mediaRecorderRef = useRef(null);
const recordedChunksRef = useRef([]);

const pitchShiftNodeRef = useRef(null);
const voicePresetRef = useRef('normal');

const { alert } = prompt;

const createPitchShifter = (audioCtx, initialPitch = 1.0) => {
  const bufferSize = 4096;
  const node = audioCtx.createScriptProcessor(bufferSize, 1, 1);

  let pitch = initialPitch;
  const grainSize = 1024;
  const overlap = 0.5;
  let inputBuffer = new Float32Array(bufferSize * 2);
  let writeIndex = 0;

  node.setPitch = (newPitch) => { pitch = newPitch; };

  node.onaudioprocess = (e) => {
    const input = e.inputBuffer.getChannelData(0);
    const output = e.outputBuffer.getChannelData(0);

    // Décalage de pitch simple par ré-échantillonnage granulaire avec overlap
    const hopIn = grainSize * (1 - overlap);
    const hopOut = hopIn / pitch;

    for (let i = 0; i < output.length; i++) {
      const srcIndex = i * pitch;
      const idx0 = Math.floor(srcIndex) % input.length;
      const idx1 = (idx0 + 1) % input.length;
      const frac = srcIndex - Math.floor(srcIndex);
      // Interpolation linéaire pour lisser le résultat
      output[i] = input[idx0] * (1 - frac) + input[idx1] * frac;
    }
  };

  return node;
};

const startRecording = () => {
  if (!outgoingStreamRef.current) return;

  recordedChunksRef.current = [];

  // Choix du format selon support navigateur/WebView
  const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9,opus')
    ? 'video/webm;codecs=vp9,opus'
    : MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus')
    ? 'video/webm;codecs=vp8,opus'
    : 'video/webm';

  try {
    const recorder = new MediaRecorder(outgoingStreamRef.current, {
      mimeType,
      videoBitsPerSecond: 2_000_000 // 2 Mbps, ajustable
    });

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) recordedChunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      saveRecording();
    };

    recorder.onerror = (e) => {
      console.error('Erreur enregistrement:', e.error);
      alert("Erreur pendant l'enregistrement.");
      setIsRecording(false);
    };

    recorder.start(1000); // chunk toutes les 1s, évite de tout garder en mémoire d'un coup
    mediaRecorderRef.current = recorder;
    setIsRecording(true);
  } catch (err) {
    console.error('Impossible de démarrer l\'enregistrement:', err);
    alert("L'enregistrement n'est pas supporté sur cet appareil.");
  }
};

const stopRecording = () => {
  mediaRecorderRef.current?.stop();
  mediaRecorderRef.current = null;
  setIsRecording(false);
};

const toggleRecording = () => {
  isRecording ? stopRecording() : startRecording();
};

const saveRecordingNative = (blob, filename) => {
  const targetDir = window.cordova.file.externalRootDirectory + 'Download/';
  window.resolveLocalFileSystemURL(targetDir, (dirEntry) => {
    dirEntry.getFile(filename, { create: true, exclusive: false }, (fileEntry) => {
      fileEntry.createWriter((writer) => {
        writer.onwriteend = () => {
          alert(t('discussion.download', { filename }));
        };
        writer.onerror = () => {
          alert(t('discussion.download_error'));
        };
        writer.write(blob);
      });
    }, () => {
      alert(t('discussion.download_error_create'));
    });
  }, () => {
    alert(t('discussion.download_error_file'));
  });
};
const saveRecording = () => {
  const blob = new Blob(recordedChunksRef.current, { type: 'video/webm' });
  const filename = `visio_${new Date().toISOString().replace(/[:.]/g, '-')}.webm`;

  if (window.cordova) {
    saveRecordingNative(blob, filename);
  } else {
    const url = URL.createObjectURL(blob);
    downloadBlob(url, filename);
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  }
};
const downloadBlob = (url, filename) => {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

const createAnalyser = (stream, ctx) => {
  const source = ctx.createMediaStreamSource(stream);
  const analyser = ctx.createAnalyser();
  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = 0.6;
  source.connect(analyser);
  return analyser;
};



const handleCustomBackground = (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  // Nettoyer l'ancienne URL objet si elle existe, pour éviter les fuites mémoire
  if (customBgUrl) URL.revokeObjectURL(customBgUrl);

  const objectUrl = URL.createObjectURL(file);
  setCustomBgUrl(objectUrl);

  const img = new Image();
  img.onload = () => {
    bgImageRef.current = img;
    bgIdRef.current = 'custom';
    setBgId('custom');
  };
  img.onerror = () => {
    console.warn('Erreur chargement image personnalisée');
    alert("Impossible de charger cette image.");
  };
  img.src = objectUrl;

  // Réinitialiser l'input pour permettre de resélectionner le même fichier plus tard
  e.target.value = '';
};

const processAudioTrack = async (rawAudioTrack) => {
  const audioCtx = new AudioContext({ sampleRate: 48000 });
 //console.log(audioCtx.state);
if (audioCtx.state === "suspended") {
        await audioCtx.resume();
    }

   // console.log(audioCtx.state);

  audioCtxRef.current = audioCtx;

  const source = audioCtx.createMediaStreamSource(new MediaStream([rawAudioTrack]));
  const destination = audioCtx.createMediaStreamDestination();

  const compressor = audioCtx.createDynamicsCompressor();
  compressor.threshold.setValueAtTime(-35, audioCtx.currentTime);
  compressor.knee.setValueAtTime(30, audioCtx.currentTime);
  compressor.ratio.setValueAtTime(4, audioCtx.currentTime);
  compressor.attack.setValueAtTime(0.003, audioCtx.currentTime);
  compressor.release.setValueAtTime(0.25, audioCtx.currentTime);

  const highpass = audioCtx.createBiquadFilter();
  highpass.type = 'highpass';
  highpass.frequency.setValueAtTime(80, audioCtx.currentTime);

  const lowpass = audioCtx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.setValueAtTime(12000, audioCtx.currentTime);

  const bassFilter = audioCtx.createBiquadFilter();
  bassFilter.type = 'lowshelf';
  bassFilter.frequency.setValueAtTime(200, audioCtx.currentTime);
  bassFilter.gain.setValueAtTime(0, audioCtx.currentTime);
  bassFilterRef.current = bassFilter;

  const trebleFilter = audioCtx.createBiquadFilter();
  trebleFilter.type = 'highshelf';
  trebleFilter.frequency.setValueAtTime(3000, audioCtx.currentTime);
  trebleFilter.gain.setValueAtTime(0, audioCtx.currentTime);
  trebleFilterRef.current = trebleFilter;

  // ✅ Pitch shifter
  const pitchShifter = createPitchShifter(audioCtx, 1.0);
  pitchShiftNodeRef.current = pitchShifter;

  source.connect(highpass);
  highpass.connect(lowpass);
  lowpass.connect(bassFilter);
  bassFilter.connect(trebleFilter);
  trebleFilter.connect(compressor);
  compressor.connect(destination);

  return destination.stream.getAudioTracks()[0];
};

const [voicePreset, setVoicePreset] = useState('normal');

const applyVoicePreset = (preset) => {
  const presets = {
    normal: 1.0,
    aigu:   1.4,  // voix suraiguë type "chipmunk"
    grave:  0.7,  // voix grave type "robot/monstre"
  };
  pitchShiftNodeRef.current?.setPitch(presets[preset]);
  voicePresetRef.current = preset;
  setVoicePreset(preset);
};
  // ── 1. MediaPipe ────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const { ImageSegmenter, FilesetResolver } = await import('@mediapipe/tasks-vision');
        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision/wasm'
        );
        const segmenter = await ImageSegmenter.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite',
            delegate: 'CPU',
          },
          outputCategoryMask: false,
          outputConfidenceMasks: true,
          runningMode: 'VIDEO',
        });
        segmentationRef.current = segmenter;
        setBgReady(true);
      } catch (e) {
        console.warn('MediaPipe non disponible:', e);
      }
    };
    load();
    return () => {
      stopProcessingLoop();
      segmentationRef.current?.close?.();
    };
  }, []);

//console.log('🟢 RENDER VERSION TEST', Math.random());
  // ── 2. Rendu canvas ─────────────────────────────────────────────────────────
  const renderFrame = useCallback((results) => {
    const canvas = canvasRef.current;
    const video  = rawVideoRef.current;
    if (!canvas || !video) return;

    const w = canvas.width;
    const h = canvas.height;
    const currentBg = bgIdRef.current;
    const ctx = canvas.getContext('2d');

    ctx.clearRect(0, 0, w, h);

    if (currentBg === 'none' || !results) {
      ctx.drawImage(video, 0, 0, w, h);
      return;
    }

    const confidenceMask = results.confidenceMasks?.[0];
    if (!confidenceMask) { ctx.drawImage(video, 0, 0, w, h); return; }
//console.log('Mask dims:', confidenceMask.width, confidenceMask.height, 'Canvas dims:', w, h);

    const maskFloat = confidenceMask.getAsFloat32Array();
    confidenceMask.close();
//console.log('maskFloat.length:', maskFloat.length, 'expected:', w * h);
    const fn = renderFrame;
    if (!fn._pc) {
      fn._pc  = document.createElement('canvas');
      fn._pct = fn._pc.getContext('2d', { willReadFrequently: true });
    }
    fn._pc.width = w; fn._pc.height = h;
  //  const pct = fn._pct;
const pct = fn._pc.getContext('2d', { willReadFrequently: true });

    pct.clearRect(0, 0, w, h);
    pct.drawImage(video, 0, 0, w, h);
    const vd = pct.getImageData(0, 0, w, h);
    const od = pct.createImageData(w, h);
    const px = vd.data, out = od.data;

    for (let i = 0; i < w * h; i++) {
      const p = i * 4;
      //const alpha = maskFloat[i] > 0.5 ? 1 : 0; 
      out[p]   = px[p];
      out[p+1] = px[p+1];
      out[p+2] = px[p+2];
      out[p + 3] = Math.round(maskFloat[i] * 255);
    }
    pct.putImageData(od, 0, 0);

    if (currentBg === 'blur') {
      ctx.filter = 'blur(16px)';
      ctx.drawImage(video, 0, 0, w, h);
      ctx.filter = 'none';
    } else if (bgImageRef.current) {
      ctx.drawImage(bgImageRef.current, 0, 0, w, h);
    } else {
      ctx.fillStyle = '#111'; ctx.fillRect(0, 0, w, h);
    }
    ctx.drawImage(fn._pc, 0, 0, w, h);
  }, []);


  // ── 3. Boucle RAF ───────────────────────────────────────────────────────────
  const startProcessingLoop = useCallback(() => {
    const loop = () => {
      const seg   = segmentationRef.current;
      const video = rawVideoRef.current;
      if (!seg || !video || video.readyState < 2) {
        // Pas de segmentation dispo : copie brute
        const canvas = canvasRef.current;
        if (canvas && video && video.readyState >= 2) {
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        }
        animFrameRef.current = requestAnimationFrame(loop);
        return;
      }
      try {
        if (bgIdRef.current !== 'none') {
          const results = seg.segmentForVideo(video, performance.now());
          renderFrame(results);
        } else {
          renderFrame(null);
        }
      } catch (e) {}
      animFrameRef.current = requestAnimationFrame(loop);
    };
    animFrameRef.current = requestAnimationFrame(loop);
  }, [renderFrame]);

  const stopProcessingLoop = useCallback(() => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
  }, []);

  // ── 4. Démarrage caméra + streams ───────────────────────────────────────────
  const startLocalStream = async () => {
  try {
    // Un seul getUserMedia, vidéo + audio ensemble (comme la V1)
    const rawVid = await navigator.mediaDevices.getUserMedia({
      video: { width: 640, height: 480, facingMode: 'user' },
      audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,

    sampleRate: 48000,
    sampleSize: 16,
    channelCount: 1,

    latency: 0.01
}

    });

    rawVideoStreamRef.current = rawVid;
    rawVideoRef.current.srcObject = rawVid;
    rawVideoRef.current.muted = true;
    await rawVideoRef.current.play().catch(() => {});

    await new Promise((resolve) => {
      if (rawVideoRef.current.videoWidth > 0) return resolve();
      rawVideoRef.current.onloadedmetadata = () => resolve();
    });

    const actualWidth  = rawVideoRef.current.videoWidth;
    const actualHeight = rawVideoRef.current.videoHeight;
    const canvas = canvasRef.current;
    canvas.width  = actualWidth;
    canvas.height = actualHeight;

    const canvasStream = canvas.captureStream(30);
    videoStreamRef.current = canvasStream; // pour toggleVideo

    // ✅ Déplacé ici, après que canvasStream existe
    const rawAudioTrack = rawVid.getAudioTracks()[0];
    rawAudioTrackRef.current = rawAudioTrack; // pour toggleAudio

    const processedAudioTrack = await processAudioTrack(rawAudioTrack);

    // ✅ Analyser pour détecter quand TOI vous parlez
const localAnalyser = createAnalyser(
  new MediaStream([processedAudioTrack]),
  audioCtxRef.current
);
analysersRef.current['local'] = localAnalyser;

    outgoingStreamRef.current = new MediaStream([
      ...canvasStream.getVideoTracks(),
      processedAudioTrack
    ]);

    startProcessingLoop();

    if (localVideoRef.current) {
      localVideoRef.current.srcObject = canvasStream;
      localVideoRef.current.muted = true;
    }

  } catch (err) {
    console.error('Erreur caméra/micro:', err);
    alert("Impossible d'accéder à la caméra ou au micro.");
  }
};

  const checkMediaPermissions = () => {
    const perms = window.cordova.plugins.permissions;
    const list  = [perms.CAMERA, perms.RECORD_AUDIO];
    perms.hasPermission(list, (s) => {
      if (!s?.hasPermission) {
        perms.requestPermissions(list, (r) => {
          r?.hasPermission ? startLocalStream() : alert('🚫 Permissions refusées.');
        }, () => alert('❌ Erreur permissions.'));
      } else startLocalStream();
    }, () => alert('❌ Erreur permissions.'));
  };

  useEffect(() => {
    if (window.cordova?.plugins?.permissions) checkMediaPermissions();
    else startLocalStream();
  }, []);

  // ── 5. Fond virtuel ──────────────────────────────────────────────────────────
  const applyBackground = useCallback(async (newBgId) => {
    bgIdRef.current = newBgId;
    setBgId(newBgId);
    bgImageRef.current = null;

    if (newBgId === 'none' || newBgId === 'blur') return;

    const bg = BACKGROUNDS.find(b => b.id === newBgId);
    if (!bg?.src) return;

    await new Promise((res) => {
      const img = new Image();
      // Sur Cordova file://, crossOrigin bloque le chargement — ne pas le mettre
      if (!window.cordova) img.crossOrigin = 'anonymous';
      img.onload = () => { bgImageRef.current = img; res(); };
      img.onerror = (e) => {
        console.warn('Erreur chargement image fond:', bg.src, e);
        res();
      };
      // Sur Cordova : chemin relatif depuis www/
      img.src = window.cordova
        ? bg.src  // ex: "backgrounds/office.jpg" → www/backgrounds/office.jpg
        : bg.src;
    });
  }, []);

  // ── 6. Init WebRTC ──────────────────────────────────────────────────────────
  useEffect(() => {
    const initCall = async () => {
      let actualParticipants = initialParticipants;
      if (!initialParticipants && discussionId) {
        try {
          const res = await api.get(`/api/discussions/${discussionId}/participants`);
          actualParticipants = res.data;
          setParticipants(actualParticipants);
        } catch (err) { console.error(err); return; }
      }
     // if (!videoStreamRef.current || !audioStreamRef.current) {
      //  console.warn('Streams non prêts');
       // return;
     // }
      if (!outgoingStreamRef.current) {
  console.warn('Stream non prêt');
  return;
}
      socket.emit('register', { userId: user.id });
      if (isCaller) {
        socket.emit('start-group-call', {
          discussionId,
          participants: actualParticipants.map(p => p._id),
        });
      }
      if (!timerRef.current) {
        timerRef.current = setInterval(() => setCallDuration(p => p + 1), 1000);
      }
    };

    // Attendre que les deux streams soient prêts
    const waitStreams = setInterval(() => {
  if (outgoingStreamRef.current) {
    clearInterval(waitStreams);
    initCall();
  }
}, 100);

    return () => {
      clearInterval(waitStreams);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    };
  }, [socket, discussionId, isCaller]);

  // ── 7. Listeners socket ──────────────────────────────────────────────────────
  useEffect(() => {
  socket.on('group-call-peers', ({ socketIdMap }) => {
    if (!outgoingStreamRef.current) return;
    Object.entries(socketIdMap).forEach(([fromUserId, socketId]) => {
      const peer = createPeer(socketId, fromUserId, true);
      peersRef.current[socketId] = peer;
      setPeers(prev => ({ ...prev, [socketId]: peer }));
    });
  });
  return () => socket.off('group-call-peers');
}, [socket]);

useEffect(() => {
  const pendingOffers = {};

  socket.on('start-group-call', ({ caller }) => {
    if (!outgoingStreamRef.current) return;
    const peer = createPeer(caller.socketId, caller.userId, false);
    peersRef.current[caller.socketId] = peer;
    if (pendingOffers[caller.socketId]) {
      peer.signal(pendingOffers[caller.socketId]);
      delete pendingOffers[caller.socketId];
    }
  });

  socket.on('offer', ({ offer, from }) => {
    const peer = peersRef.current[from];
    if (peer) peer.signal(offer);
    else pendingOffers[from] = offer;
  });

  socket.on('answer', ({ answer, from }) => {
    const peer = peersRef.current[from];
    if (peer) peer.signal(answer);
  });

  return () => {
    socket.off('start-group-call');
    socket.off('offer');
    socket.off('answer');
  };
}, [socket]);

  // ── 8. Création peer — vidéo canvas + audio séparés ─────────────────────────
  const createPeer = (remoteSocketId, remoteUserId, initiator) => {
//console.log('🔵 createPeer appelé pour', remoteUserId, 'initiator:', initiator);
  const peer = new Peer({
    initiator,
    trickle: false,
    stream: outgoingStreamRef.current,
    config: {
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' }
      ]
    }
  });
//console.log('📦 Tracks envoyées:', outgoingStreamRef.current.getTracks().map(t => t.kind));
peer._pc.oniceconnectionstatechange = () => {
    //console.log('🧊 ICE STATE →', remoteUserId, peer._pc.iceConnectionState);
  };
  peer.on('signal', (signal) => {
    socket.emit(initiator ? 'offer' : 'answer', {
      [initiator ? 'offer' : 'answer']: signal,
      to: remoteSocketId,
      fromUserId: user.id,
    });
  });
peer.on('stream', (remoteStream) => {
  const videoRef = remoteVideoRefs.current[remoteUserId]?.current;
  if (!videoRef) return;
  videoRef.srcObject = remoteStream;
  videoRef.muted = false;
  videoRef.play().catch(() => {});

  // ✅ Analyser pour détecter quand CE participant parle
  try {
    const remoteAudioCtx = new AudioContext();
    const analyser = createAnalyser(remoteStream, remoteAudioCtx);
    analysersRef.current[remoteUserId] = analyser;
  } catch (e) {
    console.warn('Analyser remote impossible:', e);
  }
});
peer.on('connect', () => {
   // console.log('✅ Peer connecté', remoteUserId);
  });
  peer.on('stream', (remoteStream) => {
 //console.log('📥 Stream reçu, tracks:', remoteStream.getTracks().map(t => `${t.kind}:${t.enabled}:${t.readyState}`));
    const videoRef = remoteVideoRefs.current[remoteUserId]?.current;
//console.log('🎯 videoRef trouvé:', !!videoRef);
    if (!videoRef) return;
    videoRef.srcObject = remoteStream;
    videoRef.muted = false;
 videoRef.play().then(() => {
     // console.log('▶️ play() réussi');
    }).catch(err => {
      console.error('❌ play() échoué:', err.name, err.message);
    });
  });

  peer.on('error', err => console.error('[peer error]', remoteUserId, err));
  return peer;
};

useEffect(() => {
  const dataArray = new Uint8Array(256);
  const THRESHOLD = 18; // à ajuster selon sensibilité voulue

  const measure = () => {
    const newSpeaking = {};
    Object.entries(analysersRef.current).forEach(([id, analyser]) => {
      analyser.getByteFrequencyData(dataArray);
      const avg = dataArray.reduce((sum, v) => sum + v, 0) / dataArray.length;
      newSpeaking[id] = avg > THRESHOLD;
    });
    setSpeakingMap(prev => {
      // Évite un re-render si rien n'a changé
      const changed = Object.keys(newSpeaking).some(k => prev[k] !== newSpeaking[k]);
      return changed ? newSpeaking : prev;
    });
    speakingRafRef.current = requestAnimationFrame(measure);
  };

  speakingRafRef.current = requestAnimationFrame(measure);

  return () => {
    if (speakingRafRef.current) cancelAnimationFrame(speakingRafRef.current);
  };
}, []);
  // ── 9. Contrôles ─────────────────────────────────────────────────────────────
  const toggleVideo = () => {
    const track = videoStreamRef.current?.getVideoTracks()[0];
    if (track) { track.enabled = !track.enabled; setVideoEnabled(track.enabled); }
  };

  const toggleAudio = () => {
  const track = rawAudioTrackRef.current;
  if (track) { track.enabled = !track.enabled; setAudioEnabled(track.enabled); }
};

  const hangUp = () => {
  stopProcessingLoop();
  Object.values(peersRef.current).forEach(p => p.destroy());
  videoStreamRef.current?.getTracks().forEach(t => t.stop());
  rawVideoStreamRef.current?.getTracks().forEach(t => t.stop());
  rawAudioTrackRef.current?.stop();

Object.values(analysersRef.current).forEach(analyser => {
  analyser.disconnect();
});
analysersRef.current = {};
if (speakingRafRef.current) cancelAnimationFrame(speakingRafRef.current);

if (mediaRecorderRef.current && isRecording) {
  mediaRecorderRef.current.stop(); // déclenchera saveRecording() via onstop
}

  if (audioCtxRef.current) {
    audioCtxRef.current.close();
    audioCtxRef.current = null;
  }

  if (customBgUrl) {
  URL.revokeObjectURL(customBgUrl);
}
  if (rawVideoRef.current) rawVideoRef.current.srcObject = null;
  setPeers({});
  peersRef.current = {};
  setCallInProgress(false);
  navigate('/discussion');
};

  const formatDuration = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return [h > 0 ? String(h).padStart(2,'0') : null, String(m).padStart(2,'0'), String(sec).padStart(2,'0')].filter(Boolean).join(':');
  };

  const handleFullscreen = () => localVideoRef.current?.requestFullscreen?.();

  const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

const handlePiP = async () => {
  const v = localVideoRef.current;

  if (isFirefox) {
    // Firefox gère son propre PiP via une icône native au survol de la vidéo
    alert("Sur Firefox, utilisez l'icône Picture-in-Picture qui apparaît directement sur la vidéo au survol.");
    return;
  }

  if (!v || !document.pictureInPictureEnabled) return;

  try {
    document.pictureInPictureElement
      ? await document.exitPictureInPicture()
      : await v.requestPictureInPicture();
  } catch (e) {
    console.error('Erreur PiP:', e);
  }
};

  const otherParticipants = participants.filter(p => String(p._id) !== myUserId);
  const gridColumns = otherParticipants.length <= 1 ? 1 : isMobile ? 2 : 4;

const [showEq, setShowEq] = useState(false);
const [showVoicePanel, setShowVoicePanel] = useState(false);
  // ── 10. Rendu ────────────────────────────────────────────────────────────────
  return (
    <div>
      <h2>{t('discussion.videocall')}</h2>
<div style={{
  position: 'fixed',
  top: 80,
  right: 12,
  background: 'rgba(0,0,0,0.6)',
  color: 'white',
  fontSize: 13,
  padding: '4px 12px',
  borderRadius: '20px',
  zIndex: 1001,
  fontVariantNumeric: 'tabular-nums' // évite que les chiffres "sautent" visuellement
}}>
  ⏱️ {formatDuration(callDuration)}
</div>
      <video ref={rawVideoRef} autoPlay playsInline muted style={{ display: 'none' }} />
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      <div className="local-video-container">
        <h4>{t('discussion.me')}</h4>
        <video ref={localVideoRef} autoPlay playsInline muted className="video-player"
	style={{
      outline: speakingMap['local'] ? '3px solid #4fc3f7' : 'none',
      boxShadow: speakingMap['local'] ? '0 0 16px rgba(79,195,247,0.7)' : 'none',
      transition: 'outline 0.15s ease, box-shadow 0.15s ease',
      borderRadius: '8px'
    }} />
      </div>

      {/* Barre de contrôle */}
      <div style={{ display:'flex', flexDirection:'row', alignItems:'center', gap:'0.8rem', background:'rgba(0,0,0,0.6)', padding:'5px 20px', borderRadius:'30px', zIndex:1000 }}>
        <button onClick={handleFullscreen} title="Plein écran" style={btn()}>🖥️</button>
        {!isMobile && document.pictureInPictureEnabled && (
  <button onClick={handlePiP} title="PiP" style={btn()}>🪟</button>
)}
        <button onClick={toggleVideo} title={videoEnabled ? 'Désactiver caméra' : 'Activer caméra'} style={btn({ color: videoEnabled ? '#fff' : '#888' })}>
          {videoEnabled ? '🎥' : '🚫🎥'}
        </button>
        <button onClick={toggleAudio} title={audioEnabled ? 'Couper micro' : 'Activer micro'} style={btn({ color: audioEnabled ? '#fff' : '#888' })}>
          {audioEnabled ? '🎙️' : '🔇'}
        </button>
<button onClick={() => setShowEq(v => !v)} title="Égalisation"
  style={btn({ color: showEq ? '#4fc3f7' : '#fff' })}>
  🎚️
</button>
<button onClick={() => setShowVoicePanel(v => !v)} title="Voix"
  style={btn({ color: voicePreset !== 'normal' ? '#4fc3f7' : '#fff' })}>
  🗣️
</button>
        <button onClick={() => setBgPanelOpen(p => !p)} disabled={!bgReady}
          style={btn({ background: bgId !== 'none' ? 'rgba(255,255,255,0.15)' : 'none', border: bgId !== 'none' ? '1px solid rgba(255,255,255,0.4)' : 'none', borderRadius:'8px', padding:'4px 8px', opacity: bgReady ? 1 : 0.4 })}>
          🖼️
        </button>
<button onClick={toggleRecording} title={isRecording ? 'Arrêter l\'enregistrement' : 'Enregistrer'}
  style={btn({ color: isRecording ? '#ff5252' : '#fff' })}>
  {isRecording ? '⏺️' : '⚪'}
</button>
        <button onClick={hangUp} style={{ fontSize:14, backgroundColor:'red', border:'none', borderRadius:'50%', width:28, height:28, color:'#fff', cursor:'pointer', display:'flex', justifyContent:'center', alignItems:'center' }}>❌</button>
        
      </div>
{showVoicePanel && (
  <div style={{
    display: 'flex', gap: '8px',
    background: 'rgba(0,0,0,0.75)',
    padding: '10px 16px', borderRadius: '16px',
    marginTop: '8px', justifyContent: 'center'
  }}>
    {[
      { id: 'normal', label: `🎤 ${t('discussion.normal')}` },
      { id: 'aigu',   label: `🐿️ ${t('discussion.aigus') }`},
      { id: 'grave',  label: `🤖 ${t('discussion.graves') }`},
    ].map(v => (
      <button key={v.id} onClick={() => applyVoicePreset(v.id)}
        style={{ padding:'6px 12px', borderRadius:'8px', cursor:'pointer', fontSize:13,
          background: voicePreset === v.id ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
          border: voicePreset === v.id ? '1px solid rgba(255,255,255,0.6)' : '1px solid rgba(255,255,255,0.2)',
          color:'#fff' }}>
        {v.label}
      </button>
    ))}
  </div>
)}
{isRecording && (
  <div style={{
    position: 'absolute',
    top: 80,
    left: 12,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    background: 'rgba(0,0,0,0.6)',
    padding: '4px 10px',
    borderRadius: '20px',
    color: '#ff5252',
    fontSize: 12,
    zIndex: 1001
  }}>
    <span style={{
      width: 8, height: 8, borderRadius: '50%',
      background: '#ff5252',
      animation: 'pulse 1.2s infinite'
    }} />
    REC
  </div>
)}
{showEq && (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    gap: '0.4rem',
    background: 'rgba(0,0,0,0.75)',
    padding: '10px 16px',
    borderRadius: '16px',
    marginTop: '8px',
    maxWidth: '260px',
    marginLeft: 'auto',
    marginRight: 'auto',
    color: 'white',
    fontSize: 12
  }}>
    <label>
      {t('discussion.graves')}
      <input
        type="range" min="-12" max="12" step="1" defaultValue="0"
        style={{ width: '100%' }}
        onChange={(e) => {
          bassFilterRef.current?.gain.setTargetAtTime(
            Number(e.target.value), audioCtxRef.current.currentTime, 0.05
          );
        }}
      />
    </label>
    <label>
       {t('discussion.aigus')}
      <input
        type="range" min="-12" max="12" step="1" defaultValue="0"
        style={{ width: '100%' }}
        onChange={(e) => {
          trebleFilterRef.current?.gain.setTargetAtTime(
            Number(e.target.value), audioCtxRef.current.currentTime, 0.05
          );
        }}
      />
    </label>
  </div>
)}
      {bgPanelOpen && bgReady && (
  <div style={{ display:'flex', gap:'10px', flexWrap:'wrap', background:'rgba(0,0,0,0.75)', borderRadius:'12px', padding:'12px 16px', marginTop:'8px' }}>
<div style={{ color:'#fff', fontSize:'0.9em', flexBasis: '100%' }}> {t('discussion.fonds')}</div>
    {BACKGROUNDS.map(bg => (

      <button key={bg.id} onClick={() => { applyBackground(bg.id); setBgPanelOpen(false); }}
        style={{ padding:'6px 14px', borderRadius:'8px', cursor:'pointer', fontSize:13,
          background: bgId === bg.id ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
          border: bgId === bg.id ? '1px solid rgba(255,255,255,0.6)' : '1px solid rgba(255,255,255,0.2)',
          color:'#fff' }}>
        {bg.label}
      </button>
    ))}

    {/* ✅ Bouton pour choisir une image personnalisée */}
    <button onClick={() => fileInputRef.current?.click()}
      style={{ padding:'6px 14px', borderRadius:'8px', cursor:'pointer', fontSize:13,
        background: bgId === 'custom' ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.08)',
        border: bgId === 'custom' ? '1px solid rgba(255,255,255,0.6)' : '1px solid rgba(255,255,255,0.2)',
        color:'#fff' }}>
      📁  {t('discussion.personnalise')}
    </button>

    <input
      ref={fileInputRef}
      type="file"
      accept="image/*"
      onChange={(e) => { handleCustomBackground(e); setBgPanelOpen(false); }}
      style={{ display: 'none' }}
    />
  </div>
)}

      <div className="remote-video-grid" style={{ gridTemplateColumns:`repeat(${gridColumns}, 1fr)` }}>
        <h4 style={{ gridColumn:'1 / -1' }}>{t('participants.participants')}</h4>
        {otherParticipants.map(p => (
          <div key={p._id} className="participant-video">
            <p>{p.username}</p>
            <video
              ref={ref => {
                if (!remoteVideoRefs.current[p._id]) remoteVideoRefs.current[p._id] = { current: ref };
                else remoteVideoRefs.current[p._id].current = ref;
              }}
              autoPlay playsInline className="video-player"
		style={{
    outline: speakingMap[p._id] ? '3px solid #4fc3f7' : 'none',
    boxShadow: speakingMap[p._id] ? '0 0 16px rgba(79,195,247,0.7)' : 'none',
    transition: 'outline 0.15s ease, box-shadow 0.15s ease',
    borderRadius: '8px'
  }}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

const btn = (extra = {}) => ({ fontSize:18, background:'none', border:'none', color:'#fff', cursor:'pointer', padding:'4px', ...extra });
const subtitleStyle = {
  position: 'absolute',
  bottom: 8,
  left: '50%',
  transform: 'translateX(-50%)',
  background: 'rgba(0,0,0,0.75)',
  color: 'white',
  fontSize: 13,
  padding: '4px 10px',
  borderRadius: '8px',
  maxWidth: '90%',
  textAlign: 'center',
  whiteSpace: 'normal',
  pointerEvents: 'none'
};

export default VideoCall;